/**
 * Доход: награда за волну и офлайн-доход (окно «с возвращением»).
 *
 * Время приходит аргументом (мс Unix от сервера); engine часов не читает.
 */
import {
  COINS_MAX,
  PERMILLE,
  TICK_RATE,
  type BalanceData,
  type FriendRewardDef,
  type OfflineIncome,
  type StatModifier,
  type WaveOutcome,
} from "@newfungame/contracts";
import { applyModifiers, selectModifiers } from "./modifiers";
import { offlineCapSeconds } from "./progression";

/** Волна с боссом (каждая `bossEvery`-я). */
export function isBossWave(b: BalanceData, wave: number): boolean {
  return wave > 0 && wave % b.waves.bossEvery === 0;
}

/** Базовая награда за пройденную волну `base + perWave · (wave − 1)` — без удвоения за босса. */
export function waveBaseReward(b: BalanceData, wave: number): number {
  const r = b.economy.waveReward;
  const w = Math.max(1, wave);
  const late = r.lateGrowth;
  if (!late || w <= late.fromWave) return r.base + r.perWave * (w - 1);
  // после fromWave — геометрический рост, целыми шагами вниз (детерминированно); потолок — безопасное целое с запасом
  let v = r.base + r.perWave * (late.fromWave - 1);
  for (let i = late.fromWave; i < w && v < REWARD_CAP; i++) {
    const soft = late.softFromWave !== undefined && i >= late.softFromWave;
    const growth = soft ? (late.softGrowthPermille ?? late.growthPermille) : late.growthPermille;
    v = Math.floor((v * (PERMILLE + growth)) / PERMILLE);
  }
  return Math.min(v, REWARD_CAP);
}

/** Монеты каждому из пары за друга: от лучшей волны получателя. */
export function friendRewardCoins(b: BalanceData, def: FriendRewardDef, bestWave: number): number {
  return def.rewardWaves * waveBaseReward(b, bestWave);
}

/** Начисление в кошелёк с потолком {@link COINS_MAX}: лишнее сгорает, число остаётся точным. */
export function creditCoins(balance: number, delta: number): number {
  return Math.min(COINS_MAX, balance + delta);
}

/** Потолок базовой награды за волну (запас до `MAX_SAFE_INTEGER` для суммы с убийствами и `coinGain`). */
const REWARD_CAP = 10 ** 12;

export interface WaveRewardInput {
  wave: number;
  outcome: WaveOutcome;
  /** Сырые монеты за убитых (сумма `coins` из данных зомби и босса), ещё без `coinGain`. */
  killCoins: number;
  /** Модификаторы забежки и Tech (усиления, звёзды, `techModifiers`); берутся только глобальные `coinGain`. */
  modifiers?: readonly StatModifier[];
}

export interface WaveReward {
  /** Награда за волну после удвоения за босса; 0 — волна проиграна. */
  base: number;
  /** Монеты за убитых (сырые). */
  kills: number;
  /** Итого с `coinGain`: `(base + kills) · Π(1 + coinGain)`, вниз. Это `WaveResult.coins`. */
  total: number;
}

/**
 * Награда за волну. Пройдена — база (босс: ×(1 + bossBonus), т. е. ×2) плюс убийства; проиграна — только убийства
 * («монеты остаются»). Множитель `coinGain` применяется один раз к сумме — симулятор не должен умножать монеты за
 * убитых сам.
 */
export function waveReward(b: BalanceData, input: WaveRewardInput): WaveReward {
  let base = 0;
  if (input.outcome === "cleared") {
    base = waveBaseReward(b, input.wave);
    if (isBossWave(b, input.wave)) {
      base = Math.floor((base * (PERMILLE + b.economy.waveReward.bossBonusPermille)) / PERMILLE);
    }
  }
  const kills = Math.max(0, input.killCoins);
  const gained = applyModifiers(base + kills, selectModifiers(input.modifiers ?? [], "coinGain", "global"));
  const total = Math.min(gained, COINS_MAX);
  return { base, kills, total };
}

/** Темп последней пройденной волны: сколько монет она принесла и за сколько тиков. */
export interface WavePace {
  coins: number;
  ticks: number;
}

export interface OfflineInput {
  /** С какого момента игрок отсутствует (последняя активность / последнее получение офлайн-дохода), мс Unix. */
  sinceMs: number;
  /** Сейчас (время сервера), мс Unix. */
  nowMs: number;
  /** Последняя пройденная волна; `null` — ещё ни одной. */
  lastWave: WavePace | null;
  /** Уровни Tech (склад поднимает потолок). */
  tech: Readonly<Record<string, number>>;
}

/**
 * Офлайн-доход: `ratePermille` (50 %) от темпа последней пройденной волны (монет в секунду) за время отсутствия,
 * не больше потолка (8 ч + склад). Отсутствие короче `minSeconds` (5 мин) — `coins = 0`, окно не показывается.
 * `coinGain` повторно не применяется — он уже в монетах волны.
 */
export function offlineIncome(b: BalanceData, input: OfflineInput): OfflineIncome {
  const capSeconds = offlineCapSeconds(b, input.tech);
  const away = Math.max(0, Math.floor((input.nowMs - input.sinceMs) / 1000));
  const seconds = Math.min(away, capSeconds);
  const pace = input.lastWave;
  if (away < b.economy.offline.minSeconds || !pace || pace.ticks <= 0 || pace.coins <= 0) {
    return { coins: 0, seconds, capSeconds };
  }
  const coins =
    (BigInt(pace.coins) * BigInt(TICK_RATE) * BigInt(seconds) * BigInt(b.economy.offline.ratePermille)) /
    (BigInt(pace.ticks) * BigInt(PERMILLE));
  return { coins: coins > BigInt(COINS_MAX) ? COINS_MAX : Number(coins), seconds, capSeconds };
}
